/**
 * The 15 keywords (CR §8, rules inventory §5) — where a card's keywords come from, and the
 * accessors the rules consult.
 *
 * Keywords are **engine-native**: a card module never implements one, it only declares it,
 * and even that is generated from LorcanaJSON's `keyword`/`keywordValue` fields. That is why
 * 231 keyword-only cards in the game need no hand-authored module at all (SPEC.md §5.3).
 *
 * A card's keywords come from four places, all merged here:
 *
 * 1. the printed keyword abilities in `CardDefinition.abilities` (with their numeric values),
 * 2. `CardDefinition.keywords` as a fallback for definitions that carry only names,
 * 3. hand-authored `{ kind: 'keyword' }` abilities from the catalog's ability registry,
 * 4. keywords granted at runtime — by another card's static ability, or by a `grant-keyword`
 *    effect's modifier record.
 *
 * "+N" keywords stack; every other keyword is idempotent (CR §8.1.2).
 *
 * ## Where each keyword is actually implemented
 *
 * | Keyword | Hook |
 * |---|---|
 * | Alert | `legalChallengeTargets` — ignores the Evasive limiter |
 * | Bodyguard | `legalChallengeTargets` (must-choose) + the `enter-exerted` frame |
 * | Boost | an engine-derived activated ability (`abilitiesOf`) paying `boostCostOf`, putting a card under through `stacks.ts` |
 * | Challenger +N | `strengthOf`, while a `challenge` frame names this card as the challenger |
 * | Evasive | `legalChallengeTargets` |
 * | Reckless | `legalTurnActions` — no quest option, no end-turn while it can challenge |
 * | Resist +N | `dealDamage`, on the `deal` verb only |
 * | Rush | `canChallenge` — bypasses drying, but not `canQuest`/`canExertForAction` |
 * | Shift N | `shiftCostOf` here, `costs.ts` for the variants and base sets, `payments.ts` for the payment, `stacks.ts` for the pile |
 * | Singer N | `singerValueOf`, spent by `payments.ts` when a song is sung |
 * | Sing Together N | `singTogetherCostOf`, enumerated as multi-singer payments by `payments.ts` |
 * | Support | a keyword-derived triggered ability (`keywordAbilities`) |
 * | Underdog | `costOf` |
 * | Vanish | a keyword-derived triggered ability on the `card-chosen` event |
 * | Ward | `canBeChosenBy`, consulted by every target pool |
 */
import type { CardCatalog, CardDefinition } from '../cards.ts';
import type { InstanceId } from '../ids.ts';
import { grantedKeywords, lostKeywords } from '../modifiers.ts';
import type { CardInstance, GameState } from '../state.ts';
import { instanceAt } from '../state.ts';
import type { Ability, KeywordName, StaticAbility } from './ir.ts';
import { KEYWORD_NAMES, STACKING_KEYWORDS } from './ir.ts';

/** One keyword on one card, with its numeric parameter where it has one. */
export interface KeywordInstance {
  readonly keyword: KeywordName;
  readonly value: number | null;
}

const KEYWORD_SET = new Set<string>(KEYWORD_NAMES);
const STACKING_SET = new Set<string>(STACKING_KEYWORDS);

/**
 * Keywords the engine plays **correctly and completely** today. A card whose only rules text
 * is one of these needs no module (`isSupportedCard`); a card carrying any other keyword is
 * refused at game start until the ticket that finishes it lands, because playing it with the
 * keyword silently ignored would be a wrong game (SPEC.md §5.3).
 */
export const COMPLETE_KEYWORDS: readonly KeywordName[] = [
  'Alert',
  'Bodyguard',
  'Boost',
  'Challenger',
  'Evasive',
  'Reckless',
  'Resist',
  'Rush',
  'Shift',
  'Sing Together',
  'Singer',
  'Support',
  'Underdog',
  'Vanish',
  'Ward',
];

/**
 * Keywords whose semantics wait on a later ticket, with the ticket that finishes them.
 *
 * **Empty since ticket 12.** Shift's payment landed in 09 and its card stack in 12; Boost's
 * `{I}` runs through `abilityInkCostOf` and its put-under is the same stack. All 15 keywords
 * are now played completely, so a keyword-only card of any kind needs no module — which is
 * what `isKeywordOnlyAndComplete` tells `isSupportedCard`. The map stays because it is the
 * declared shape for the next keyword a set introduces.
 */
export const PARTIAL_KEYWORDS: Readonly<Record<string, string>> = {};

const COMPLETE_SET = new Set<string>(COMPLETE_KEYWORDS);

/** Normalise a printed keyword name, including the Shift family's variants. */
export function normaliseKeyword(printed: string): KeywordName | null {
  const name = printed.trim();
  if (KEYWORD_SET.has(name)) return name as KeywordName;
  // `Puppy Shift`, `Universal Shift`, `Temporary Shift`, `Duo Shift`, … (rules inventory §5).
  if (/\bShift$/u.test(name)) return 'Shift';
  return null;
}

/** The keywords printed on a card definition, with their values. */
export function printedKeywords(def: CardDefinition): KeywordInstance[] {
  const found: KeywordInstance[] = [];
  for (const ability of def.abilities) {
    if (ability.type !== 'keyword' || ability.keyword === undefined) continue;
    const keyword = normaliseKeyword(ability.keyword);
    if (keyword === null) continue;
    found.push({ keyword, value: ability.keywordValueNumber ?? null });
  }
  if (found.length > 0) return found;
  // Definitions that carry only the name list (test fixtures, thin catalogs).
  for (const printed of def.keywords) {
    const keyword = normaliseKeyword(printed);
    if (keyword !== null) found.push({ keyword, value: null });
  }
  return found;
}

/** The hand-authored ability IR for a printed card, if the catalog serves any. */
export function authoredAbilities(
  catalog: CardCatalog,
  defId: number,
): readonly Ability[] | undefined {
  return catalog.abilities?.(defId);
}

// ---------------------------------------------------------------------------
// Memoized views of printed data
//
// These cache *printed and authored card data* per catalog, never a computed characteristic:
// the catalog is immutable and shared by reference with every clone (`GameState.catalog`), so
// a view derived from it alone cannot go stale. SPEC.md §4.5's no-cache rule is about live
// modifier state, which never enters these.
// ---------------------------------------------------------------------------

const NO_STATICS: readonly StaticAbility[] = [];
const NO_KEYWORDS: readonly KeywordInstance[] = [];

const STATICS_MEMO = new WeakMap<CardCatalog, Map<number, readonly StaticAbility[]>>();
const BASE_KEYWORDS_MEMO = new WeakMap<CardCatalog, Map<number, readonly KeywordInstance[]>>();

function memoFor<T>(store: WeakMap<CardCatalog, Map<number, T>>, catalog: CardCatalog): Map<number, T> {
  let byDef = store.get(catalog);
  if (byDef === undefined) {
    byDef = new Map();
    store.set(catalog, byDef);
  }
  return byDef;
}

// A game runs against one catalog, so the WeakMap hop is paid once per catalog switch, not
// once per read. The strong reference keeps at most one catalog alive — the one in use.
let staticsCatalog: CardCatalog | null = null;
let staticsByDef: Map<number, readonly StaticAbility[]> | null = null;
let baseKeywordsCatalog: CardCatalog | null = null;
let baseKeywordsByDef: Map<number, readonly KeywordInstance[]> | null = null;

/** The `static` abilities among a card's authored IR — the continuous-static sources. */
export function staticAbilitiesOf(
  catalog: CardCatalog,
  defId: number,
): readonly StaticAbility[] {
  if (catalog !== staticsCatalog) {
    staticsCatalog = catalog;
    staticsByDef = memoFor(STATICS_MEMO, catalog);
  }
  const byDef = staticsByDef as Map<number, readonly StaticAbility[]>;
  let statics = byDef.get(defId);
  if (statics === undefined) {
    const found: StaticAbility[] = [];
    for (const ability of catalog.abilities?.(defId) ?? NO_STATICS) {
      if (ability.kind === 'static') found.push(ability);
    }
    statics = found.length === 0 ? NO_STATICS : found;
    byDef.set(defId, statics);
  }
  return statics;
}

/**
 * A card's keywords from its printed definition plus its authored `{ kind: 'keyword' }` IR —
 * sources 1–3 of `keywordsOf`, which depend on nothing but the catalog. Shared and read-only.
 */
export function baseKeywordsOf(
  catalog: CardCatalog,
  defId: number,
): readonly KeywordInstance[] {
  if (catalog !== baseKeywordsCatalog) {
    baseKeywordsCatalog = catalog;
    baseKeywordsByDef = memoFor(BASE_KEYWORDS_MEMO, catalog);
  }
  const byDef = baseKeywordsByDef as Map<number, readonly KeywordInstance[]>;
  let keywords = byDef.get(defId);
  if (keywords === undefined) {
    const def = catalog.get(defId);
    const found: KeywordInstance[] = def === undefined ? [] : printedKeywords(def);
    for (const ability of catalog.abilities?.(defId) ?? NO_STATICS) {
      if (ability.kind === 'keyword') {
        found.push({ keyword: ability.keyword, value: ability.value ?? null });
      }
    }
    keywords = found.length === 0 ? NO_KEYWORDS : found;
    byDef.set(defId, keywords);
  }
  return keywords;
}

/**
 * Every keyword on one card in this game, from all four sources — minus the ones a continuous
 * effect has taken away (CR §6.6.5). "Loses Reckless" is suppression, not subtraction: it
 * removes the keyword however the card came by it, printed included.
 */
export function keywordsOf(state: GameState, instance: CardInstance): KeywordInstance[] {
  const base = baseKeywordsOf(state.catalog, instance.defId);
  const granted = grantedByStatics(state, instance);
  const lost = lostKeywords(state, instance);
  // Nothing granted, nothing lost — the memoized base list is the answer. It is shared:
  // keyword lists are read, never mutated, by every caller.
  if (granted.length === 0 && lost.length === 0) return base as KeywordInstance[];
  const found = base.concat(granted);
  return lost.length === 0 ? found : found.filter((each) => !lost.includes(each.keyword));
}

/**
 * Keywords a continuous effect gives this card — another card's static ability, or a resolved
 * `grant-keyword` effect's modifier record (CR §6.4.2). Computed on read, never stored, so a
 * card that arrives under an active static has the keyword the moment it lands.
 *
 * The whole model lives in `modifiers.ts`; this is one of its two read paths (the other is
 * `bonusFor` in `characteristics.ts`).
 */
function grantedByStatics(state: GameState, instance: CardInstance): KeywordInstance[] {
  return grantedKeywords(state, instance);
}

/** Whether this card has the keyword at all. */
export function hasKeyword(state: GameState, instance: CardInstance, keyword: KeywordName): boolean {
  return keywordsOf(state, instance).some((each) => each.keyword === keyword);
}

/** Same, by instance id. */
export function instanceHasKeyword(
  state: GameState,
  id: InstanceId,
  keyword: KeywordName,
): boolean {
  return hasKeyword(state, instanceAt(state, id), keyword);
}

/**
 * The numeric parameter of a keyword: **summed** for the "+N" keywords that stack
 * (Challenger, Resist) and taken at its maximum otherwise (CR §8.1.2). `0` when the card
 * does not have the keyword at all, so callers can add it unconditionally.
 */
export function keywordValue(
  state: GameState,
  instance: CardInstance,
  keyword: KeywordName,
): number {
  const stacking = STACKING_SET.has(keyword);
  let total = 0;
  let best = 0;
  for (const each of keywordsOf(state, instance)) {
    if (each.keyword !== keyword) continue;
    const value = each.value ?? 0;
    total += value;
    best = Math.max(best, value);
  }
  return stacking ? total : best;
}

/** Challenger +N: this character gets +N {S} **while challenging** (CR §8.4). */
export function challengerBonusOf(state: GameState, instance: CardInstance): number {
  return keywordValue(state, instance, 'Challenger');
}

/** Resist +N: damage **dealt** to this card is reduced by N (CR §8.9). */
export function resistOf(state: GameState, instance: CardInstance): number {
  return keywordValue(state, instance, 'Resist');
}

/** Shift N: the alternate cost to play this on one of your matching characters (CR §8.10). */
export function shiftCostOf(state: GameState, instance: CardInstance): number | null {
  return hasKeyword(state, instance, 'Shift')
    ? keywordValue(state, instance, 'Shift')
    : null;
}

/**
 * Singer N: this character counts as cost N for singing (CR §8.11) — never less than its
 * real ink cost, which is what a Singer value below the printed cost means in practice.
 */
export function singerValueOf(state: GameState, instance: CardInstance): number {
  const def = state.catalog.get(instance.defId);
  const printed = def?.cost ?? 0;
  if (!hasKeyword(state, instance, 'Singer')) return printed;
  return Math.max(printed, keywordValue(state, instance, 'Singer'));
}

/** Sing Together N: the total ink cost of exerted singers needed (CR §8.12). */
export function singTogetherCostOf(state: GameState, instance: CardInstance): number | null {
  return hasKeyword(state, instance, 'Sing Together')
    ? keywordValue(state, instance, 'Sing Together')
    : null;
}

/** Boost N {I}: the ink cost of the once-per-turn put-under ability (CR §8.3). */
export function boostCostOf(state: GameState, instance: CardInstance): number | null {
  return hasKeyword(state, instance, 'Boost') ? keywordValue(state, instance, 'Boost') : null;
}

/**
 * Ward: **opponents** can't choose this card when resolving effects (CR §8.15). Challenges
 * are not "choosing" for Ward purposes, so `legalChallengeTargets` never consults this.
 */
export function canBeChosenBy(state: GameState, target: CardInstance, chooser: number): boolean {
  if (target.controller === chooser) return true;
  // Abilities function while their card is **in play** unless the card says otherwise
  // (CR §6.1.12), so a Ward character sitting in a discard protects nothing — which is what
  // lets "shuffle a card from any discard into its player's deck" reach it.
  if (target.zone !== 'play') return true;
  return !hasKeyword(state, target, 'Ward');
}

/**
 * Whether every scrap of a card's rules text is a keyword the engine plays completely.
 *
 * A card carrying a keyword in `PARTIAL_KEYWORDS` is **not** supported: playing it with the
 * keyword silently ignored would be a wrong game, and SPEC.md §5.3 forbids partial
 * implementation. That map is empty since ticket 12 — every keyword is complete — so this now
 * turns on whether the card's text is *only* keywords.
 */
export function isKeywordOnlyAndComplete(def: CardDefinition): boolean {
  if (def.type !== 'Character' && def.type !== 'Item' && def.type !== 'Location') return false;
  if (def.abilities.length === 0 && def.keywords.length === 0) return false;
  for (const ability of def.abilities) {
    if (ability.type !== 'keyword') return false;
    const keyword = ability.keyword === undefined ? null : normaliseKeyword(ability.keyword);
    if (keyword === null || !COMPLETE_SET.has(keyword)) return false;
  }
  for (const printed of def.keywords) {
    const keyword = normaliseKeyword(printed);
    if (keyword === null || !COMPLETE_SET.has(keyword)) return false;
  }
  // Anything beyond the keyword lines (a named ability, an action effect) needs a module.
  return def.abilities.length > 0 || def.keywords.length > 0;
}
